import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Chip,
  Field,
  FormActions,
  FormSection,
  PageHeader,
  RadioCards,
  Textarea,
} from '../../../ui';
import StepperVistoria from '../../../components/vistoria/StepperVistoria';
import ModalConfirmacao from '../../../components/modal/confirmacao/ModalConfirmacao';

import { RootState } from '../../../store/store';
import {
  selectChecklistItems,
  selectChecklistObservacao,
  setItems,
  setItemStatus,
  setObservacao,
} from '../../../store/checklistAcessoriosSlice';
import {
  atualizarVistoriaAccessories,
  buscarVistoriaPorId,
} from '../../../api/vistoria/vistoriaApi';
import {
  mesclarRespostas,
  OPCOES_CHECKLIST,
  StatusAcessorio,
} from '../../../utils/vistoriaAcessorios';
import { VISTORIA_ASSINATURA, VISTORIA_CAPTURA, VISTORIA_LAUDO } from '../routes';

/**
 * `/vistoria/checklist-acessorios` — **etapa 2 do fluxo de vistoria** (reescrita
 * na at-64, frente D/D2).
 *
 * ## O que mudou de comportamento
 *
 *   1. **A tela diz em que etapa o consultor está** (`StepperVistoria` "Etapa 2 de
 *      4"), com as bolinhas **dinâmicas e navegáveis** lidas do banco. Antes ela
 *      não tinha nem cabeçalho: só um botão "Home" solto no canto.
 *   2. **Item sem resposta ganha erro JUNTO DELE**, e a tela **rola até o
 *      primeiro** — o `toast.warn` genérico ("Faltam N itens") saiu. É o defeito
 *      que a spec 03 §4 nomeia: *"não consigo avançar e não sei por quê"* é o pior
 *      estado possível no celular, e com 23 itens dizer "preencha todos" não diz
 *      QUAIS faltam.
 *   3. **As 3 opções viram `RadioCards`** do kit (alvo de 44px, setas do teclado
 *      navegam). A cor por opção do CSS antigo (Ruim vermelho, Não existe cinza)
 *      saiu: a escolha é marcada por borda e fundo, e o **texto** diz qual é
 *      (spec 02 §9). A cor por grupo continua no **laudo**, onde é resultado.
 *   4. **A lista dos 23 acessórios saiu da view** para `utils/vistoriaAcessorios.ts`
 *      — o merge era feito em dois lugares aqui, com `as any` em três.
 *
 * ⚠️ **O que NÃO mudou:** a reidratação do banco ao retomar um rascunho, o merge
 * que preserva as respostas, o `PATCH accessories` com `{ items, observacao }` e
 * as **mesmas 23 chaves**, a confirmação de "marcar todos como Bom" e o desvio de
 * `origemEdicao` (volta ao laudo em vez de seguir para a assinatura).
 */
const VistoriaChecklistPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const voltar = useVoltar(VISTORIA_CAPTURA());

  const formRef = React.useRef<HTMLDivElement>(null);

  /**
   * Aberta pela seção "Editar" do laudo (at-24): ao salvar, volta para o laudo em
   * vez de seguir para a assinatura — corrigir um item do checklist não pode
   * obrigar o associado a assinar de novo.
   */
  const origemEdicao = (location.state as any)?.origemEdicao === true;

  const vistoriaId = useSelector((state: RootState) => state.vistoria.vistoriaId);
  const associadoId = useSelector((state: RootState) => state.associado.associadoId);
  const carroId = useSelector((state: RootState) => state.vistoria.vehicle?.id);

  const items = useSelector(selectChecklistItems);
  const observacao = useSelector(selectChecklistObservacao);

  /** Só destaca os itens vazios depois de o consultor tentar continuar. */
  const [mostrarPendentes, setMostrarPendentes] = React.useState(false);
  /** Confirmação de "marcar todos como Bom" quando há respostas a sobrescrever. */
  const [confirmarTodosBom, setConfirmarTodosBom] = React.useState(false);
  const [salvando, setSalvando] = React.useState(false);

  /**
   * Garante que o slice tenha exatamente os 23 acessórios, preservando o que já
   * estiver respondido.
   *
   * ⚠️ Continua sendo a tela quem popula o slice — ele nasce **vazio** desde a
   * at-64 (antes vinha com 46 legendas de foto que nunca chegavam aqui).
   */
  React.useEffect(() => {
    const mesclados = mesclarRespostas(items);

    const igual =
      items.length === mesclados.length &&
      items.every(
        (item, indice) =>
          item.key === mesclados[indice].key &&
          item.label === mesclados[indice].label &&
          item.tipo === mesclados[indice].tipo &&
          item.status === mesclados[indice].status,
      );

    if (!igual) dispatch(setItems(mesclados));
  }, [dispatch, items]);

  /**
   * Reidrata as respostas já salvas no banco ao retomar um rascunho (at-24).
   *
   * Esta tela lê o checklist do Redux, que é limpo no logout e ao retomar — sem
   * buscar do banco, o consultor que já respondeu parte dos itens veria a tela em
   * branco e perderia o trabalho. Roda uma vez por `vistoriaId`.
   */
  const [hidratado, setHidratado] = React.useState(false);

  React.useEffect(() => {
    if (!vistoriaId || hidratado) return undefined;

    let ativo = true;

    buscarVistoriaPorId({ vistoriaId })
      .then((resposta) => {
        if (!ativo) return;

        const salvos = Array.isArray(resposta?.accessories?.items)
          ? resposta.accessories?.items ?? []
          : [];

        if (salvos.some((item) => !!item?.status)) {
          dispatch(setItems(mesclarRespostas(salvos)));
        }

        const obsSalva = resposta?.accessories?.observacao;
        if (typeof obsSalva === 'string' && obsSalva.trim()) {
          dispatch(setObservacao(obsSalva));
        }
      })
      .catch(() => {
        // Vistoria nova ou falha na leitura: segue com a tela em branco, que é o
        // comportamento anterior. Não vale bloquear o preenchimento por isso.
      })
      .finally(() => {
        if (ativo) setHidratado(true);
      });

    return () => {
      ativo = false;
    };
  }, [vistoriaId, hidratado, dispatch]);

  const preenchidos = items.filter((item) => item.status !== null).length;
  const faltando = items.length - preenchidos;
  /** Itens já respondidos com algo diferente de BOM — seriam sobrescritos. */
  const respostasDivergentes = items.filter(
    (item) => item.status !== null && item.status !== 'BOM',
  ).length;
  const todosJaBom = items.length > 0 && items.every((item) => item.status === 'BOM');

  const aplicarTodosComoBom = () => {
    dispatch(setItems(items.map((item) => ({ ...item, status: 'BOM' as StatusAcessorio }))));
    setMostrarPendentes(false);
    setConfirmarTodosBom(false);
  };

  const marcarTodosComoBom = () => {
    // Só confirma quando há resposta deliberada a perder (Ruim / Não existe) —
    // preencher item vazio não precisa de confirmação.
    if (respostasDivergentes > 0) {
      setConfirmarTodosBom(true);
      return;
    }

    aplicarTodosComoBom();
  };

  const continuar = async () => {
    if (faltando > 0) {
      setMostrarPendentes(true);

      /*
       * Rola até o primeiro item sem resposta — padrão das at-58/59.
       *
       * ⚠️ Chamada DEFENSIVA: `scrollIntoView` não existe no jsdom, e o teste
       * desta tela clica em "Continuar" com itens em branco.
       */
      const primeiroVazio = items.find((item) => item.status === null);
      if (primeiroVazio) {
        const alvo = formRef.current?.querySelector(`[data-field="${primeiroVazio.key}"]`);
        if (typeof (alvo as HTMLElement | null)?.scrollIntoView === 'function') {
          (alvo as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }

    setMostrarPendentes(false);

    if (vistoriaId) {
      setSalvando(true);
      try {
        await atualizarVistoriaAccessories({
          vistoriaId,
          accessories: { items, observacao },
        });
      } catch {
        toast.error('Falha ao salvar checklist da vistoria.');
        setSalvando(false);
        return;
      }
      setSalvando(false);
    }

    // Edição: o checklist salvo é tudo que precisava mudar — volta ao laudo sem
    // passar pela assinatura, que já pode estar armazenada e válida.
    if (origemEdicao) {
      toast.success('Checklist atualizado.');
      navigate(VISTORIA_LAUDO());
      return;
    }

    navigate(VISTORIA_ASSINATURA(), { state: { associadoId, carroId } });
  };

  return (
    <ShellSaac
      cabecalho={
        <PageHeader sobretitulo="Vistoria" titulo="Checklist de acessórios" aoVoltar={voltar} />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-3xl md:tw-p-6">
          <div className="tw-bg-white md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-border-b tw-border-superficie-borda">
              <StepperVistoria etapa={2} />
            </div>

            <div ref={formRef} className="tw-p-4 md:tw-p-6">
              {/*
                A contagem no topo + a ação em massa. "Selecionar todos como Bom"
                é útil porque na prática a maioria dos 23 itens é "Bom" (at-24);
                fica como ação secundária para não competir com o "Continuar".
              */}
              <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <Chip variante={faltando === 0 ? 'ok' : 'neutro'}>
                    {preenchidos} de {items.length}
                  </Chip>
                  <span className="tw-text-xs tw-text-texto-suave">itens preenchidos</span>
                </div>

                <Botao
                  variante="secundario"
                  tamanho="sm"
                  disabled={todosJaBom}
                  onClick={marcarTodosComoBom}
                  title={
                    todosJaBom
                      ? 'Todos os itens já estão marcados como Bom.'
                      : `Marca os ${items.length} itens como Bom; você ajusta depois os que não estiverem.`
                  }
                >
                  Selecionar todos como Bom
                </Botao>
              </div>

              {/*
                O aviso do que falta, em texto e com `role="alert"` — o leitor de
                tela anuncia sem o consultor ir procurar. Substitui o `toast.warn`,
                que passava e não dizia quais itens eram.
              */}
              {mostrarPendentes && faltando > 0 && (
                <p
                  role="alert"
                  className="tw-mb-4 tw-rounded-xl tw-border tw-border-danger/40 tw-border-l-4 tw-border-l-danger tw-bg-danger-bg tw-px-3 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-danger"
                >
                  {faltando === 1
                    ? 'Falta 1 item sem resposta — ele está destacado abaixo.'
                    : `Faltam ${faltando} itens sem resposta — eles estão destacados abaixo.`}
                </p>
              )}

              <div className="tw-flex tw-flex-col tw-gap-6">
                <FormSection titulo="Acessórios" icone="check">
                  <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
                    {items.map((item) => (
                      <Field
                        key={item.key}
                        grupo
                        rotulo={item.label}
                        campo={item.key}
                        erro={
                          mostrarPendentes && item.status === null
                            ? 'Responda este item'
                            : undefined
                        }
                      >
                        <RadioCards
                          nome={item.key}
                          opcoes={OPCOES_CHECKLIST}
                          valor={item.status ?? ''}
                          aoMudar={(valor) =>
                            dispatch(
                              setItemStatus({
                                key: item.key,
                                status: valor as StatusAcessorio,
                              }),
                            )
                          }
                        />
                      </Field>
                    ))}
                  </div>
                </FormSection>

                <FormSection titulo="Observação" icone="contato">
                  <Field
                    rotulo="Observação"
                    dica="Opcional. Vai no laudo e no PDF da vistoria."
                    campo="observacao"
                  >
                    <Textarea
                      value={observacao}
                      onChange={(evento) => dispatch(setObservacao(evento.target.value))}
                      placeholder="Digite a observação"
                    />
                  </Field>
                </FormSection>
              </div>

              <FormActions>
                <Botao
                  variante="primario"
                  larguraTotal
                  carregando={salvando}
                  disabled={salvando}
                  onClick={() => void continuar()}
                >
                  {/* "Continuar" no fluxo novo (segue para a assinatura);
                      "Salvar checklist" na edição, onde a ação termina aqui. */}
                  {origemEdicao ? 'Salvar checklist' : 'Continuar'}
                </Botao>
              </FormActions>
            </div>
          </div>
        </div>
      </div>

      {/* Legado, de propósito: confirmação sempre em modal, nunca `window.confirm`. */}
      <ModalConfirmacao
        aberto={confirmarTodosBom}
        titulo="Marcar todos como Bom"
        mensagem={`Isto vai substituir ${respostasDivergentes} resposta(s) já preenchida(s) como "Ruim" ou "Não existe".`}
        detalhe="Os itens ainda em branco também serão marcados como Bom."
        rotuloConfirmar="Marcar todos"
        onConfirmar={aplicarTodosComoBom}
        onCancelar={() => setConfirmarTodosBom(false)}
      />
    </ShellSaac>
  );
};

export default VistoriaChecklistPage;
